import axios from "axios";
import {
    isArray,
    isObject,
    mapValues
} from "lodash";
import i18n from 'i18next';
import { enqueueSnackbar } from 'notistack';
import { store } from "./Store";
import { SetTokenExpiresAt } from "./Actions/User";
import GetCookie from './Components/Common/Functions/GetCookie';
import {
    getLicenceRestrictionMessageKey,
    getRequestLicenceAction
} from './Components/Common/Functions/licenceRestrictions';
import { Rule } from "@mui/icons-material";
import {
    filterVersionsAndVariants
} from "./Components/ItineraryType/utils/filterVersionsAndVariants";
import { AppState } from "./Reducers/Reducers";

// ─── Token expiry tracking ────────────────────────────────────────────────────
// Capture token_expires_at / expires_at from API responses and dispatch to Redux
// so UI components (e.g. expiry warnings) can react to it.
const scheduleExpiryWarning = (expiresAt: string) => {
    store.dispatch(SetTokenExpiresAt(expiresAt));
};

// ─── Trip version on product searches ─────────────────────────────────────────
// A trip version can carry custom currency rates, so every product search / pricing
// call must tell the API which version it prices for, otherwise the API falls back to
// the trip's current version and converts prices with the wrong rates.
const TRIP_VERSION_PATHS = [
    '/accommodations/minimal_prices/',
    '/accommodations/rooms_prices/',
    '/flights/',
    '/flights/availability/',
    '/flights/service_catalogue/',
    '/flights/option/',
    '/flights/seat_map/',
    '/cars/vehicle_prices/',
    '/cars/gestour_vehicle_prices/',
    '/cars/check_availability/',
    '/cars/vehicle_check_contract/',
    '/transfers/transfer_prices/',
    '/trains/search/',
    '/ferry/search/',
    '/insurances/search_quote/',
    '/custom-products/minimal_price/',
    '/custom-products/product_prices/',
    '/custom-products/product_transfer_prices/',
    '/custom-transfers/get_transfer_prices/',
    '/additional-product/product_from_hotel/'
];

// These endpoints read their params from the payload instead of the query string when
// they are called with POST.
const TRIP_VERSION_BODY_PATHS = [
    '/insurances/search_quote/',
    '/flights/option/',
    '/flights/seat_map/',
    '/flights/service_catalogue/'
];

// ─── Licence restrictions ─────────────────────────────────────────────────────
// Some licences are read-only on trips, carts and bookings (see licenceRestrictions.ts).
// The buttons that lead to those writes are disabled, but the app has far too many write
// paths to gate every one of them by hand, so this is the backstop: it refuses the call
// here instead of letting the API answer 403, which keeps the feedback immediate and
// spares every caller from having to handle a rejection it never expected.
// It is inert for anyone whose licence allows the action, which is the vast majority.
const getRequestRestrictionKey = (
    method: string | undefined,
    url: string
): string | null => {
    const action = getRequestLicenceAction(method, url);

    if (!action) {
        return null;
    }

    return getLicenceRestrictionMessageKey(
        (store.getState() as AppState).user.user,
        action
    );
};

// Clear the token cookie (domain-aware, matching SetCookie convention).
const clearTokenCookie = () => {
    const expires = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
    const host = window.location.host;
    const domainPart = host.includes("localhost") ? "" : `;Domain=${host}`;
    document.cookie = `token=;${expires};path=/${domainPart}`;
};

axios.interceptors.request.use(
    (request) => {
        if (request.url) {
            const isSameOrigin = new URL(
                request.url?.startsWith('http') ?
                    request.url :
                    window.location.origin + request.url,
            ).origin === new URL(
                API_HREF.startsWith('http') ?
                    API_HREF :
                    window.location.origin
            ).origin;

            if (
                !request.headers.get('X-Environment') &&
                isSameOrigin
            ) {
                request.headers.set('X-Environment', 'APPS');
            }
            try {
                const url = new URL(
                    request.url,
                    !request.baseURL && !request.url.startsWith('http') ?
                        window.location.origin :
                        request.baseURL
                );
                if (
                    url.pathname.endsWith('/flight/') ||
                    url.pathname.endsWith('/accommodation/') ||
                    url.pathname.endsWith('/accommodations/') ||
                    url.pathname.endsWith('/transfers/') ||
                    url.pathname.endsWith('/manual-products/') ||
                    url.pathname.endsWith('/custom-products/') ||
                    url.pathname.endsWith('/destinations/') ||
                    url.pathname.endsWith('/children/') ||
                    url.pathname.endsWith('/instances/') ||
                    url.pathname.endsWith('/custom-providers/') ||
                    url.pathname.endsWith('/circuit-category/') ||
                    url.pathname.endsWith('/auth/')
                ) {
                    request.url = request.url.replace('/v1', '/v2');
                }

                if (url.pathname.includes('/user/') && !url.pathname.includes('/crm')) {
                    request.params = {
                        ...request.params ?? {},
                        general_info: true
                    }
                }
            } catch (error) {
                if (!(error instanceof Error) || !error.message.includes('Invalid URL')) {
                    console.error(error);
                }
            }
        }

        if (window.location.pathname.includes('/provider-token')) {
            const matches = /\/provider-token\/([^\/]+)/.exec(window.location.pathname);
            const token = matches?.[1];
            if (token) {
                return {
                    ...request,
                    params: {
                        ...request.params,
                        provider_token: token
                    }
                };
            }
        }

        const requestUrl = request.url ?? '';
        const isApiRequest = requestUrl.includes(API_HREF) || requestUrl.includes(API_HREF.replace("v1", "v2"));

        if (!isApiRequest) {
            return request;
        }

        // Whether to refuse is decided on the key alone, so a missing translation can never
        // let a call through that the API is going to answer 403.
        const restrictionKey = getRequestRestrictionKey(request.method, requestUrl);

        if (restrictionKey) {
            enqueueSnackbar(i18n.t(restrictionKey) || restrictionKey, { variant: 'error' });
            // Hand back an already-aborted signal rather than throwing: this interceptor is
            // `synchronous`, and on that path axios swallows a throw and dispatches the request
            // anyway with the pre-interceptor config. `dispatchRequest` checks `signal.aborted`
            // before it reaches the adapter, so the call is rejected without ever hitting
            // the network.
            const abortedRequest = new AbortController();
            abortedRequest.abort();
            return { ...request, signal: abortedRequest.signal };
        }

        if (!request.headers['Accept']) {
            request.headers['Accept'] = 'application/json';
        }

        if (!request.headers['Content-Type']) {
            request.headers['Content-Type'] = 'application/json';
        }
        if (!request.headers['Accept-Language']) {
            request.headers['Accept-Language'] = i18n.language;
        }

        if (request.headers['X-Public-Request']) {
            delete request.headers['X-Public-Request'];
            return request;
        }

        const token = GetCookie("token");
        let url = request.url ?? '';
        url = url.startsWith('http') ? url :
            window.location.origin + url;
        if (
            token &&
            !request.headers['Authorization'] &&
            (
                !url ||
                !(new URL(url).pathname.endsWith('/instances/'))
            )
        ) {
            request.headers['Authorization'] = `Token ${token}`;
        }

        return request;
    },
    (error) => {
        return Promise.reject(error);
    },
    {
        synchronous: true
    }
);

function replaceUrlsWithCompressedPicture(data: unknown): unknown {
    if (isArray(data)) {
        return data.map((item) => {
            return replaceUrlsWithCompressedPicture(item);
        });
    }

    if (isObject(data)) {
        if (
            'compressed_picture' in data &&
            data.compressed_picture
        ) {
            if ('url' in data) {
                data.url = data.compressed_picture;
            }

            if ('thumbnail_big' in data) {
                data.thumbnail_big = data.compressed_picture;
            }

            if ('thumbnail_medium' in data) {
                data.thumbnail_medium = data.compressed_picture;
            }
        }

        return mapValues(
            data,
            (value) => replaceUrlsWithCompressedPicture(value)
        );
    }

    return data;
}

axios.interceptors.response.use(
    (response) => {
        const data = response.data;
        if (data && typeof data === 'object' && !(data instanceof Blob)) {
            if (data.token_expires_at) {
                scheduleExpiryWarning(data.token_expires_at);
            }
            if (data.expires_at && data.token) {
                scheduleExpiryWarning(data.expires_at);
            }
        }
        const request = response.config;

        if (response.data && !(response.data instanceof Blob)) {
            if (request.params?.frontFilterVersionsAndVariants) {
                return {
                    ...response,
                    data: replaceUrlsWithCompressedPicture(
                        filterVersionsAndVariants({
                            rootData: response.data,
                            user: (store.getState() as AppState).user.user
                        })
                    )
                }
            }
            return {
                ...response,
                data: replaceUrlsWithCompressedPicture(response.data)
            };
        }

        return response;
    },
    (error) => {
        if (error.response?.status === 401) {
            clearTokenCookie();
            store.dispatch(SetTokenExpiresAt(null));
            window.location.href = `${WP_BASE_HREF}home`;
        }
        return Promise.reject(error);
    }
);
